import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Radio } from "antd";
import { useTranslation } from "react-i18next";
import OlMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import { Fill, Stroke, Style, Text, Circle as CircleStyle } from "ol/style";
import { fromLonLat, toLonLat } from "ol/proj";
import { Modify } from "ol/interaction";
import { unByKey } from "ol/Observable";
import { createEmpty, extend as extendExtent, isEmpty as isExtentEmpty, containsCoordinate } from "ol/extent";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { createHereTileSource, normalizeHereLanguage } from "../../utils/hereMaps";
import { reverseGeocode } from "../../utils/hereGeocoding";
import { validCoordinates } from "../../utils/locationValidation";
import "ol/ol.css";

const ClickOnMap = ({
  basePoints = [],
  onBasePointUpdate,
  activeIndex,
  plannedRoute = [],
  shipmentType,
  onClearPoints,
  markerStyle,
  onSelectionStart,
  selectionVersion = 0,
  fitCompleteTrip = false,
}) => {
  const mapHostRef = useRef(null);
  const [satellite, setSatellite] = useState(false);
  const onSelectionStartRef = useRef(onSelectionStart);
  onSelectionStartRef.current = onSelectionStart;
  const selectionSequenceRef = useRef(0);
  useEffect(() => { selectionSequenceRef.current++; }, [selectionVersion]);
  const markerStyleRef = useRef(markerStyle);
  markerStyleRef.current = markerStyle;
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markerSourceRef = useRef(null);
  const routeSourceRef = useRef(null);
  const activeIndexRef = useRef(activeIndex);
  const basePointsRef = useRef(basePoints);
  const onBasePointUpdateRef = useRef(onBasePointUpdate);
  const hereApiKeyRef = useRef("");
  const mapLanguageRef = useRef("en");
  const translationRef = useRef((key, options) => options?.defaultValue || key);
  const hasAppliedInitialFitRef = useRef(false);
  const fittedTripRef = useRef("");
  const { t, i18n } = useTranslation();
  const { hereApiKey } = useGlobalSettings();
  const mapLanguage = useMemo(
    () => normalizeHereLanguage(i18n?.resolvedLanguage || i18n?.language || "en"),
    [i18n?.resolvedLanguage, i18n?.language]
  );

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    basePointsRef.current = basePoints;
  }, [basePoints]);

  useEffect(() => {
    onBasePointUpdateRef.current = onBasePointUpdate;
  }, [onBasePointUpdate]);

  useEffect(() => {
    hereApiKeyRef.current = hereApiKey;
  }, [hereApiKey]);

  useEffect(() => {
    mapLanguageRef.current = mapLanguage;
  }, [mapLanguage]);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useEffect(() => {
    if (!mapHostRef.current || mapRef.current) return;

    const tileLayer = new TileLayer({
      source: createHereTileSource({
        apiKey: hereApiKeyRef.current,
        language: mapLanguageRef.current,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      }),
      visible: true,
    });
    const routeSource = new VectorSource();
    const markerSource = new VectorSource();
    const routeLayer = new VectorLayer({ source: routeSource, declutter: true });
    const markerLayer = new VectorLayer({ source: markerSource, declutter: true });

    const map = new OlMap({
      target: mapHostRef.current,
      layers: [tileLayer, routeLayer, markerLayer],
      view: new View({
        zoom: 5,
        center: fromLonLat([2.021, 41.385]),
        minZoom: 2,
        maxZoom: 22,
      }),
    });

    const modify = new Modify({ source: markerSource });
    map.addInteraction(modify);

    const onMapClick = async (event) => {
      onSelectionStartRef.current?.();
      const [lng, lat] = toLonLat(event.coordinate);
      const focusedIndex = activeIndexRef.current;
      const currentBasePoints = basePointsRef.current || [];
      const targetIndex =
        focusedIndex != null
          ? focusedIndex
          : currentBasePoints.findIndex((point) => point.lat == null || point.lng == null);
      if (targetIndex === -1) return;

      const selectionSequence = ++selectionSequenceRef.current;
      // Location dialogs show the selected pin immediately, even while HERE is loading.
      if (markerStyleRef.current) {
        markerSource.getFeatures().filter(feature => feature.get("pointIndex") === targetIndex)
          .forEach(feature => markerSource.removeFeature(feature));
        const feature = new Feature({ geometry: new Point(fromLonLat([lng, lat])) });
        feature.set("pointIndex", targetIndex);
        feature.setStyle(markerStyleRef.current);
        markerSource.addFeature(feature);
      }

      const reverse = await reverseGeocode(lat, lng, hereApiKeyRef.current, {
        language: mapLanguageRef.current,
      });
      if (selectionSequence !== selectionSequenceRef.current) return;
      const address =
        reverse?.address ||
        translationRef.current("mapEditor.selectedLocation", {
          defaultValue: "Selected location",
        });
      const payload = {
        lat,
        lng,
        lookupFailed: !reverse,
        address,
        name: address,
        cityName: reverse?.cityName || "",
        postalCode: reverse?.postalCode || "",
        countryCode: reverse?.countryCode || "",
      };
      onBasePointUpdateRef.current?.(payload, targetIndex);
    };

    const onModifyEnd = async (event) => {
      onSelectionStartRef.current?.();
      const selectionSequence = ++selectionSequenceRef.current;
      const movedFeatures = event?.features?.getArray?.() || [];
      await Promise.all(
        movedFeatures.map(async (feature) => {
          const index = Number(feature.get("pointIndex"));
          if (!Number.isFinite(index)) return;
          const geometry = feature.getGeometry();
          const coordinates = geometry?.getCoordinates?.();
          if (!coordinates) return;
          const [lng, lat] = toLonLat(coordinates);
          const reverse = await reverseGeocode(lat, lng, hereApiKeyRef.current, {
            language: mapLanguageRef.current,
          });
          if (selectionSequence !== selectionSequenceRef.current) return;
          const address =
            reverse?.address ||
            translationRef.current("mapEditor.selectedLocation", {
              defaultValue: "Selected location",
            });
          const payload = {
            lat,
            lng,
            lookupFailed: !reverse,
            address,
            cityName: reverse?.cityName || "",
            postalCode: reverse?.postalCode || "",
            name:
              (basePointsRef.current?.[index]?.name || "").trim() ||
              reverse?.name ||
              address,
            countryCode:
              reverse?.countryCode ||
              basePointsRef.current?.[index]?.countryCode ||
              "",
          };
          onBasePointUpdateRef.current?.(payload, index);
        })
      );
    };

    const modifyKey = modify.on("modifyend", onModifyEnd);
    map.on("singleclick", onMapClick);

    const updateMapSize = () => map.updateSize();
    updateMapSize();
    const resizeRafId = window.requestAnimationFrame(updateMapSize);
    const resizeTimeoutId = window.setTimeout(updateMapSize, 120);
    window.addEventListener("resize", updateMapSize);
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => map.updateSize());
      resizeObserver.observe(mapHostRef.current);
    }

    mapRef.current = map;
    tileLayerRef.current = tileLayer;
    markerSourceRef.current = markerSource;
    routeSourceRef.current = routeSource;

    return () => {
      selectionSequenceRef.current++;
      map.un("singleclick", onMapClick);
      unByKey(modifyKey);
      map.removeInteraction(modify);
      window.removeEventListener("resize", updateMapSize);
      window.cancelAnimationFrame(resizeRafId);
      window.clearTimeout(resizeTimeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      map.setTarget(undefined);
      mapRef.current = null;
      tileLayerRef.current = null;
      markerSourceRef.current = null;
      routeSourceRef.current = null;
      hasAppliedInitialFitRef.current = false;
      fittedTripRef.current = "";
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    tileLayerRef.current?.setSource(
      createHereTileSource({
        apiKey: hereApiKey,
        language: mapLanguage,
        style: satellite ? "satellite.day" : "lite.day",
        imageFormat: satellite ? "jpeg" : "png8",
        includeLanguage: !satellite,
        disablePois: true,
      })
    );
    mapRef.current.render();
  }, [hereApiKey, mapLanguage, satellite]);

  useEffect(() => {
    if (!mapRef.current || !markerSourceRef.current || !routeSourceRef.current) return;

    const markerSource = markerSourceRef.current;
    const routeSource = routeSourceRef.current;
    markerSource.clear();
    routeSource.clear();

    const labelMap = {
      CONTAINER_INBOUND: ["\u26df", "\u2b07", "\u21a9"],
      CONTAINER_OUTBOUND: ["\u26df", "\u2b06", "\u21a9"],
      CONVENTIONAL_PICKUP: ["\ud83d\udce6", "\ud83c\udfc1"],
      CONVENTIONAL_DELIVERY: ["\ud83d\udce6", "\ud83c\udfc1"],
    };
    const labels = labelMap[shipmentType] || [];

    basePoints.forEach((point, idx) => {
      if (typeof point?.lat !== "number" || typeof point?.lng !== "number") return;
      const feature = new Feature({
        geometry: new Point(fromLonLat([point.lng, point.lat])),
      });
      feature.set("pointIndex", idx);
      feature.setStyle(
        markerStyle || new Style({
          image: new CircleStyle({
            radius: 8,
            fill: new Fill({ color: "#2563eb" }),
            stroke: new Stroke({ color: "#ffffff", width: 2 }),
          }),
          text: new Text({
            text: labels[idx] || String(idx + 1),
            fill: new Fill({ color: "#ffffff" }),
            font: "600 11px Arial",
            offsetY: 0,
          }),
          zIndex: 20,
        })
      );
      markerSource.addFeature(feature);
    });

    const routePoints = (Array.isArray(plannedRoute) ? plannedRoute : [])
      .filter((point) => typeof point?.latitude === "number" && typeof point?.longitude === "number")
      .map((point) => fromLonLat([point.longitude, point.latitude]));

    const fallbackPoints = basePoints
      .filter((point) => typeof point?.lat === "number" && typeof point?.lng === "number")
      .map((point) => fromLonLat([point.lng, point.lat]));

    const polylineCoordinates = routePoints.length >= 2 ? routePoints : fallbackPoints;
    if (polylineCoordinates.length >= 2) {
      const polylineFeature = new Feature({
        geometry: new LineString(polylineCoordinates),
      });
      polylineFeature.setStyle(
        new Style({
          stroke: new Stroke({
            color: "#2f54eb",
            width: 3,
          }),
          zIndex: 10,
        })
      );
      routeSource.addFeature(polylineFeature);
    }

    const combinedExtent = createEmpty();
    if (markerSource.getFeatures().length > 0) {
      extendExtent(combinedExtent, markerSource.getExtent());
    }
    if (routeSource.getFeatures().length > 0) {
      extendExtent(combinedExtent, routeSource.getExtent());
    }

    const completeTrip = fitCompleteTrip && basePoints.length >= 2
      && basePoints.every(point => validCoordinates(point?.lat, point?.lng));
    const tripKey = completeTrip
      ? JSON.stringify([basePoints.map(point => [point.lat, point.lng]), routePoints]) : "";
    if (!completeTrip) fittedTripRef.current = "";

    if (!isExtentEmpty(combinedExtent)) {
      if (!hasAppliedInitialFitRef.current || (completeTrip && fittedTripRef.current !== tripKey)) {
        mapRef.current.updateSize();
        mapRef.current.getView().fit(combinedExtent, {
          padding: [36, 36, 36, 36],
          duration: 180,
          maxZoom: 16,
        });
        hasAppliedInitialFitRef.current = true;
        if (completeTrip) fittedTripRef.current = tripKey;
      }
      if (markerStyle && basePoints.length === 1) {
        const point = fromLonLat([basePoints[0].lng, basePoints[0].lat]);
        const view = mapRef.current.getView();
        if (!containsCoordinate(view.calculateExtent(mapRef.current.getSize()), point)) view.animate({ center: point, duration: 180 });
      }
      return;
    }

    hasAppliedInitialFitRef.current = false;
  }, [basePoints, plannedRoute, shipmentType, markerStyle, fitCompleteTrip]);

  return (
    <div style={{ position: "relative" }}>
      <Radio.Group size="small" value={satellite} onChange={e => setSatellite(e.target.value)} style={{ marginBottom: 8 }}>
        <Radio.Button value={false}>{t("placeForm.road")}</Radio.Button>
        <Radio.Button value={true}>{t("fleetLayout.mapModes.satellite")}</Radio.Button>
      </Radio.Group>
      <div ref={mapHostRef} style={{ height: "420px", width: "100%" }} />
      {onClearPoints && (
        <Button
          type="default"
          size="small"
          onClick={onClearPoints}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
          }}
        >
          {t("common.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </div>
  );
};

export default ClickOnMap;
